import { io, type Socket } from 'socket.io-client';
import { SOCKET_URL } from '../lib/env';
import { getItem } from '../lib/storage';
import {
  connectionStatus,
  markDisconnected,
  markLive,
  markStaleTimerOnWaypoint,
} from '../state/connection';
import { routeStore } from '../state/routeStore';
import type { RouteEvent, RouteWaypoint } from './route';
import {
  appendRouteEvents,
  applyEmergencyEvent,
  applyTransportationEvent,
  mergeRoutePartial,
  type EmergencyEventPayload,
  type RoutePartial,
  type TransportationEventPayload,
} from './routeMerge';
import { pollNow } from './routePoll';

let instance: Socket | null = null;
const DISABLE_SOCKET_IN_DEV = import.meta.env['VITE_DISABLE_SOCKET_IN_DEV'] === 'true';
const SOCKET_TRANSPORTS = import.meta.env.DEV ? ['websocket'] : ['polling', 'websocket'];

function logRouteEvent(event: string, ...args: unknown[]): void {
  // Dev-only: `onAny` fans out every socket event (including location
  // payloads), which would spam the console and leak route data in prod.
  if (!import.meta.env.DEV) return;
  console.log(`[route:event] ${event}`, ...args);
}

// The API emits `location` as `{ route, update }` where `update` is the batch
// of new waypoints from route-track-native.ts (usually one, sometimes several
// when the device flushes a queued backlog). It is NOT `{ route_id, waypoint }`
// — matching the wrong shape here is what silently dropped every live update.
type LocationPayload = {
  route?: string;
  update?: RouteWaypoint | RouteWaypoint[];
};

type RouteEventPayload = {
  routeID?: string;
  event?: RouteEvent;
};

function waypointKey(waypoint: RouteWaypoint): string {
  return [
    waypoint.time instanceof Date ? waypoint.time.toISOString() : waypoint.time ?? '',
    waypoint.lat,
    waypoint.lon,
    waypoint.odo ?? '',
  ].join('|');
}

export function connectSocket(shareCode: string, routeId: string): Socket | null {
  // Cloudflare in front of the staging API rejects WS upgrades from
  // localhost origins with a 502, before the request reaches the Socket.IO
  // server. Keep an explicit opt-out for local work that only needs the
  // static route view + REST polling fallback.
  if (import.meta.env.DEV && DISABLE_SOCKET_IN_DEV) {
    logRouteEvent('socket:skipped', {
      reason: 'disabled in Vite dev',
      routeId,
      disableFlag: 'VITE_DISABLE_SOCKET_IN_DEV=true',
    });
    connectionStatus.value = 'offline';
    return null;
  }
  if (instance) {
    logRouteEvent('connect:reuse', { routeId });
    instance.connect();
    return instance;
  }
  const accessToken = getItem('accessToken') ?? '';
  connectionStatus.value = 'connecting';
  logRouteEvent('connect:start', { routeId, socketUrl: SOCKET_URL });
  instance = io(SOCKET_URL, {
    query: { accessToken },
    transports: SOCKET_TRANSPORTS,
  });
  instance.io.engine?.on('upgrade', (transport) => {
    logRouteEvent('transport:upgrade', { transport: transport.name, routeId });
  });
  instance.io.engine?.on('upgradeError', (error) => {
    logRouteEvent('transport:upgrade_error', {
      message: error instanceof Error ? error.message : String(error),
      routeId,
    });
  });
  instance.onAny((event, ...args) => {
    logRouteEvent(event, ...args);
  });
  const watchRoute = () => {
    const documentedPayload = { shareCode };
    logRouteEvent('route:watch', documentedPayload);
    instance?.emit('route:watch', documentedPayload, (response: unknown) => {
      logRouteEvent('route:watch:ack', { payload: 'shareCode', response });
    });

    const routePayload = { routeID: routeId, code: shareCode };
    logRouteEvent('route:watch', routePayload);
    instance?.emit('route:watch', routePayload, (response: unknown) => {
      logRouteEvent('route:watch:ack', { payload: 'routeID/code', response });
    });
  };
  let hadConnected = false;
  instance.on('connect', () => {
    logRouteEvent('connect', {
      socketId: instance?.id,
      routeId,
      transport: instance?.io.engine?.transport.name,
    });
    markLive();
    // Rooms don't replay missed events — on every reconnect, reconcile
    // immediately against REST instead of waiting out the poll cadence.
    if (hadConnected) pollNow();
    hadConnected = true;
  });
  instance.on('auth', (payload: { success?: boolean }) => {
    if (payload?.success) watchRoute();
  });
  instance.on('disconnect', (reason) => {
    logRouteEvent('disconnect', { reason, routeId });
    markDisconnected();
  });
  instance.on('connect_error', (error) => {
    logRouteEvent('connect_error', { message: error.message, routeId });
    markDisconnected();
  });
  instance.on('location', (payload: LocationPayload) => {
    // Ignore updates for any other route, in case the server ever fans
    // multiple watched routes onto one connection.
    if (payload.route && payload.route !== routeId) return;
    const incoming = payload.update;
    if (!incoming) return;
    const waypoints = Array.isArray(incoming) ? incoming : [incoming];
    if (waypoints.length === 0) return;
    const current = routeStore.value;
    if (current) {
      const existing = new Set((current.waypoints ?? []).map(waypointKey));
      const nextWaypoints = waypoints.filter((waypoint) => !existing.has(waypointKey(waypoint)));
      if (nextWaypoints.length === 0) return;
      routeStore.value = {
        ...current,
        // Guarded like the Set above: emergency-only stub routes (#143) can
        // reach the store with no waypoints array at all.
        waypoints: [...(current.waypoints ?? []), ...nextWaypoints],
      };
    }
    markStaleTimerOnWaypoint();
  });
  // `route` carries the API's FormattedRoute with only its defined fields
  // (title/destination edits per #159 land here, finish per #156). It sends
  // `id`, not `route_id` — the latter is kept as tolerance for older
  // emitters. Payloads are partial and never include waypoints/events, so
  // they are merged onto the known route, never swapped in.
  instance.on('route', (payload: RoutePartial) => {
    const incomingId = payload.id ?? payload.route_id;
    if (incomingId && incomingId !== routeId) return;
    const current = routeStore.value;
    if (!current) return;
    routeStore.value = mergeRoutePartial(current, payload);
  });
  // Emergency activate/clear per #143 — flat payload, fans out to the same
  // routes/{routeID} room route:watch joins.
  instance.on('emergency', (payload: EmergencyEventPayload) => {
    if (payload?.routeID && payload.routeID !== routeId) return;
    const current = routeStore.value;
    if (!current) return;
    routeStore.value = applyEmergencyEvent(current, payload);
  });
  // Live transport-mode changes per #149/#151. Unknown future modes are
  // dropped inside applyTransportationEvent.
  instance.on('transportation', (payload: TransportationEventPayload) => {
    if (payload?.routeID && payload.routeID !== routeId) return;
    const current = routeStore.value;
    if (!current) return;
    routeStore.value = applyTransportationEvent(current, payload);
  });
  // Journey-log events per #180 (route_started, battery_drop, stationary,
  // companion_joined, still_en_route, arrived, emergency). The broadcast is
  // the pre-save doc, so appendRouteEvents dedupes by type+time against
  // what the route read / poll already delivered.
  instance.on('route-event', (payload: RouteEventPayload) => {
    if (payload?.routeID && payload.routeID !== routeId) return;
    const current = routeStore.value;
    if (!current) return;
    const event = payload?.event;
    if (!event || typeof event.type !== 'string') return;
    routeStore.value = appendRouteEvents(current, [event]);
  });
  // Events we deliberately leave unhandled (they still show in the dev
  // onAny log): `activity` (#151 — raw Smart-Companion signal; the visible
  // chip drives off `transportation`) and `route:message` (#157 — group
  // chat; whether anonymous link watchers should read it is an open
  // product call). Staleness stays waypoint-driven on purpose: none of the
  // listeners above call markStaleTimerOnWaypoint.
  return instance;
}

export function disconnectSocket(): void {
  instance?.disconnect();
  instance = null;
}
